import { defineComponent as _defineComponent } from 'vue'
import {
    ref,
    reactive,
    PropType,
    watch,
    computed,
  } from "vue";
  import ModalWithHeader from "../ModalWithHeader.vue";
  import {
    CreateInclusionInterface,
    InclusionInterface,
    UpdateInclusionInterface,
  } from "@/types/InclusionInterface";
  import { required } from "@vuelidate/validators";
  import useVuelidate from "@vuelidate/core";
  import DropDown from "../dropdown/DropDown.vue";
  import ImageCropperModal from "./ImageCropperModal.vue";
  import store from "@/store";
  import { useToast } from "vue-toastification";
  import { read, utils } from "xlsx";
  import { processInclusionExcelData } from "@/helpers/builder/processInclusionExcelData";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'AddAndUpdateInclusionModal',
  props: {
    builderId: {
      type: String,
      required: true,
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
    mode: {
      type: String,
      required: true,
    },
    inclusion: {
      type: Object as PropType<InclusionInterface | null>,
      default: null,
    },
  },
  emits: ["close", "createInclusion", "updateInclusion"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

  const toast = useToast();
  const props = __props;

  const emits = __emit;

  const formFields = reactive({
    type: props.inclusion?.type ?? null,
    inclusions: props.inclusion?.inclusions?.length
      ? props.inclusion?.inclusions.map((inc) => ({
          ...inc,
          url: inc.url || "",
        }))
      : [
          {
            name: "",
            description: [""],
            url: "",
          },
        ],
    builderId: props.builderId,
  });

  const rules = computed(() => ({
    builderId: { required },
    type: { required },
    inclusions: {
      $each: {
        name: {
          required,
          validator: (value: string) => value.trim().length > 0,
        },
        description: {
          $each: {
            required,
            validator: (value: string) => value.trim().length > 0,
          },
        },
      },
    },
  }));

  const form = reactive(Object.assign({}, formFields));
  const v$ = useVuelidate(rules, form);

  const customErrors = ref<{ [key: number]: boolean }>({});

  const isUploadingImage = ref<boolean>(false);
  const imageToUpload = ref<{ data: string; file: File | null; index: number }>(
    {
      data: "",
      file: null,
      index: -1,
    }
  );
  const isCropperModalOpen = ref<boolean>(false);

  const xlsxError = ref<string>("");

  const validateForm = async () => {
    customErrors.value = {};

    v$.value.$touch();
    let isValid = await v$.value.$validate();

    form.inclusions.forEach((inclusion, index) => {
      if (!inclusion.name.trim()) {
        customErrors.value[index] = true;
        isValid = false;
      }
    });

    return isValid;
  };

  const addDescription = (index: number) => {
    form.inclusions[index].description.push("");
  };

  const addInclusion = () => {
    form.inclusions.push({
      name: "",
      description: [""],
      url: "",
    });
  };

  const removeDescription = (
    inclusionIndex: number,
    descriptionIndex: number
  ) => {
    if (descriptionIndex > 0) {
      form.inclusions[inclusionIndex].description.splice(descriptionIndex, 1);
    }
  };

  const removeInclusion = (inclusionIndex: number) => {
    if (inclusionIndex > 0) {
      form.inclusions.splice(inclusionIndex, 1);
    }
  };

  const close = () => {
    if (props.mode === "create") clearForm();

    if (props.mode === "update")
      assignInclusionIntoForm(props.inclusion as InclusionInterface);

    emits("close");
  };

  const confirm = async () => {
    const isValid = await validateForm();

    if (!isValid) return;

    const formDto = { ...form };

    formDto.inclusions = formDto.inclusions.map((inclusion) => ({
      ...inclusion,
      description: inclusion.description.filter(
        (desc) => desc.trim().length > 0
      ),
    }));

    if (props.mode === "create") {
      emits("createInclusion", formDto as CreateInclusionInterface);
    } else if (props.mode === "update") {
      emits("updateInclusion", formDto as UpdateInclusionInterface);
    }

    close();
  };

  const clearForm = () => {
    form.type = null;
    form.inclusions = [{ name: "", description: [""], url: "" }];
    xlsxError.value = "";
  };

  const assignInclusionIntoForm = (inclusion: InclusionInterface) => {
    form.type = inclusion.type;
    form.inclusions = inclusion.inclusions.map((inc) => ({
      ...inc,
      url: inc.url || "",
    }));
    form.builderId = inclusion.builderId;
  };

  const onUploadImage = (e: InputEvent, index: number): void => {
    const files = (e.target as HTMLInputElement).files as FileList;

    if (!files[0]) return;

    const reader = new FileReader();

    reader.readAsDataURL(files[0]);
    reader.onload = () => {
      imageToUpload.value = {
        data: reader.result as string,
        file: null,
        index,
      };
      isCropperModalOpen.value = true;
    };
  };

  async function onCropImage(file: File) {
    isCropperModalOpen.value = false;
    imageToUpload.value.file = file;

    const formData = new FormData();

    formData.append("files", file);
    uploadImageRequest(form.builderId, formData);
  }

  const uploadImageRequest = async (builderId: string, image: FormData) => {
    isUploadingImage.value = true;

    try {
      const data = await store.dispatch("inclusion/uploadImage", {
        builderId: builderId || form.builderId,
        image,
      });

      form.inclusions[imageToUpload.value.index].url = data[0]?.value?.Location;
    } catch (error) {
      toast.error("An error occurred while uploading the image");
    } finally {
      isUploadingImage.value = false;
    }
  };

  const onUploadXlsx = (e: InputEvent): void => {
    const files = (e.target as HTMLInputElement).files as FileList;

    if (!files[0]) return;

    const file = files[0];
    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    if (!validTypes.includes(file.type)) {
      xlsxError.value = "Invalid file type. Please upload an XLSX or XLS file";
      return;
    }

    xlsxError.value = "";

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = utils.sheet_to_json(worksheet);

        const processedData = processInclusionExcelData(jsonData);

        if (processedData) {
          form.inclusions = Object.entries(processedData).map(
            ([category, items]) => ({
              name: category,
              description: items,
              url: "",
            })
          );
        }
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);

        xlsxError.value = `Error reading Excel: ${errorMessage}`;
        console.error("Error reading Excel:", errorMessage);
      }
    };

    reader.onerror = () => {
      xlsxError.value = "Error reading the file";
    };

    reader.readAsArrayBuffer(file);
  };

  watch(
    () => props.inclusion,
    (inclusion) => {
      if (inclusion) {
        assignInclusionIntoForm(inclusion);
      }
    },
    {
      immediate: true,
    }
  );

const __returned__ = { toast, props, emits, formFields, rules, form, v$, customErrors, isUploadingImage, imageToUpload, isCropperModalOpen, xlsxError, validateForm, addDescription, addInclusion, removeDescription, removeInclusion, close, confirm, clearForm, assignInclusionIntoForm, onUploadImage, onCropImage, uploadImageRequest, onUploadXlsx, ref, reactive, PropType, watch, computed, ModalWithHeader, get CreateInclusionInterface() { return CreateInclusionInterface }, get InclusionInterface() { return InclusionInterface }, get UpdateInclusionInterface() { return UpdateInclusionInterface }, get required() { return required }, get useVuelidate() { return useVuelidate }, DropDown, ImageCropperModal, get store() { return store }, get useToast() { return useToast }, get read() { return read }, get utils() { return utils }, get processInclusionExcelData() { return processInclusionExcelData } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})